import { owedOutstandingMinorUnits, type Invoice, type InvoiceStatus, type LiveProforma } from '@packages/types';
import { resolveOutstandingAmountMinorUnits } from '../../lib/outstanding-amount';
import { describeInvoiceStatus, isOverdueDisplayStatus, resolveInvoiceDisplayStatus, type CopyFn, type InvoiceDisplayStatus, type InvoiceStatusFacts } from '@packages/copy';
import { useCopy } from '../../hooks/useCopy';
import { Badge } from '../ui/Badge';
import { Tooltip } from '../ui/Tooltip';

type Size = 'sm' | 'lg';

/**
 * Resolves what a row should present as its money figures: the invoice total
 * always, plus the balance still owed (the shared `owedOutstandingMinorUnits`
 * rule, which the invoice list export follows too) only where it differs from
 * the total (a partial settlement on an invoice that is still awaiting payment). `outstandingMinorUnits` is null for a draft never billed,
 * a paid or voided invoice, an awaiting-payment invoice with nothing yet
 * paid, or one that has been fully settled - in each of those cases the total
 * is the whole story and there is nothing extra to say.
 *
 * Shared by the invoices table and the dashboard's Outstanding panel so the
 * two surfaces can never disagree on which rows get the outstanding
 * treatment - see `apps/web/bill/CLAUDE.md` § "Apply UI improvements to
 * neighbouring pages".
 */
export function resolveInvoiceAmountDisplay(invoice: {
  status: InvoiceStatus;
  totalMinorUnits: number;
  outstandingAmountMinorUnits?: number;
}): { totalMinorUnits: number; outstandingMinorUnits: number | null } {
  const owed = owedOutstandingMinorUnits({
    status: invoice.status,
    outstandingMinorUnits: resolveOutstandingAmountMinorUnits(invoice),
  });
  return {
    totalMinorUnits: invoice.totalMinorUnits,
    outstandingMinorUnits: owed !== null && owed !== invoice.totalMinorUnits ? owed : null,
  };
}

type BadgeVariant = 'default' | 'warning' | 'info' | 'success' | 'error';

/** The badge takes the invoice's wire-shape pro forma; the shared rule only needs its presence. */
type InvoiceBadgeFacts = Omit<InvoiceStatusFacts, 'liveProforma'> & { liveProforma?: LiveProforma | null };

const DISPLAY_STATUS_VARIANTS: Record<InvoiceDisplayStatus, BadgeVariant> = {
  DRAFT: 'warning',
  APPROVED: 'info',
  SENT: 'info',
  PAID: 'success',
  VOIDED: 'error',
  PRO_FORMA_SENT: 'info',
  WRITTEN_OFF: 'default',
  CREDITED: 'info',
  PARTIALLY_PAID: 'warning',
  OVERDUE: 'error',
  OVERDUE_NOT_SENT: 'error',
};

/**
 * What an invoice's status presents as: the badge's label and colour. The
 * label and the override rule come from `describeInvoiceStatus`, which the
 * invoice list exports share, so the same invoice never reads differently on
 * screen, in the "Search or ask Bill" rows, or in a downloaded file.
 */
export function resolveInvoiceStatusPresentation(
  facts: InvoiceBadgeFacts,
  copy: CopyFn,
): { label: string; variant: BadgeVariant } {
  const { status, label } = describeInvoiceStatus(copy, facts);
  return { label, variant: DISPLAY_STATUS_VARIANTS[status] };
}

/** The status facts a listed invoice carries, as the status cell reads them. */
function invoiceStatusFacts(invoice: Invoice): InvoiceBadgeFacts {
  return {
    status: invoice.status,
    paymentStatus: invoice.paymentStatus,
    settledAmountMinorUnits: invoice.settledAmountMinorUnits,
    creditedAmountMinorUnits: invoice.creditedAmountMinorUnits,
    writtenOffAmountMinorUnits: invoice.writtenOffAmountMinorUnits,
    totalMinorUnits: invoice.totalMinorUnits,
    liveProforma: invoice.liveProforma,
    dueDate: invoice.dueDate,
    date: invoice.date,
  };
}

/** Whether the invoice's status reads either Overdue label, for tinting its due date to match. */
export function isInvoiceOverdue(invoice: Invoice): boolean {
  return isOverdueDisplayStatus(resolveInvoiceDisplayStatus(invoiceStatusFacts(invoice)));
}

export function InvoiceStatusBadge({
  size = 'lg',
  ...facts
}: InvoiceBadgeFacts & {
  size?: Size;
}) {
  const copy = useCopy();
  const { label, variant } = resolveInvoiceStatusPresentation(facts, copy);
  return (
    <Badge variant={variant} className={size === 'lg' ? 'px-2.5 py-1 text-sm' : undefined}>
      {label}
    </Badge>
  );
}

/**
 * Badge for a soft-deleted ("Deleted") invoice. Archived drafts keep their real
 * DRAFT status underneath, so this is derived from archivedAt, not InvoiceStatus.
 */
export function InvoiceDeletedBadge({ size = 'lg' }: { size?: Size }) {
  const copy = useCopy();
  return (
    <Badge
      variant="default"
      className={size === 'lg' ? 'px-2.5 py-1 text-sm' : undefined}
    >
      {copy('invoice:status.deleted')}
    </Badge>
  );
}

/**
 * The status cell doubles as the row's status-advance action. Clicking an
 * APPROVED invoice offers to mark it sent; clicking a SENT invoice opens
 * the Record payment dialog (when `onRecordPayment` is provided) or renders
 * as a plain non-interactive badge when omitted.
 * Terminal statuses (DRAFT, PAID, VOIDED) render as plain badges.
 * Soft-deleted invoices render a "Deleted" badge regardless of underlying status.
 * stopPropagation prevents the outer row-click from also firing.
 */
export function InvoiceStatusCell({
  invoice,
  onMarkSent,
  onRecordPayment,
  size = 'lg',
  showOverdue = true,
}: {
  invoice: Invoice;
  onMarkSent: (invoice: Invoice) => void;
  /**
   * Called when the SENT badge is clicked to record a payment. When omitted
   * the SENT badge renders as a plain non-interactive badge.
   */
  onRecordPayment?: (invoice: Invoice) => void;
  size?: Size;
  /** False where a due-date pill beside the badge already says the invoice is overdue. */
  showOverdue?: boolean;
}) {
  if (invoice.archivedAt) {
    return <InvoiceDeletedBadge size={size} />;
  }

  const action =
    invoice.status === 'APPROVED'
      ? { label: 'Click to mark as sent', run: () => onMarkSent(invoice) }
      : invoice.status === 'SENT' && onRecordPayment
        ? { label: 'Click to record payment', run: () => onRecordPayment(invoice) }
        : null;

  const { dueDate, date, ...withoutDueAxis } = invoiceStatusFacts(invoice);
  const facts = showOverdue ? { ...withoutDueAxis, dueDate, date } : withoutDueAxis;
  const badge = <InvoiceStatusBadge {...facts} size={size} />;

  if (!action) {
    return badge;
  }

  return (
    <Tooltip content={action.label}>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          action.run();
        }}
        onKeyDown={(e) => e.stopPropagation()}
        aria-label={action.label}
        className="rounded-full transition-opacity hover:opacity-80 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
      >
        {badge}
      </button>
    </Tooltip>
  );
}
